import {
	DatePipe,
	formatDate,
	NgStyle,
	NgTemplateOutlet,
} from '@angular/common';
import {
	AfterContentInit,
	AfterViewChecked,
	AfterViewInit,
	Component,
	Inject,
	Input,
	LOCALE_ID,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import {
	MAT_DIALOG_DATA,
	MatDialogActions,
	MatDialogContent,
	MatDialogRef,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
	Device,
	emptyDevice,
	isSentriVaultDevice,
} from 'src/app/models/data/device';
import { emptyAddress } from 'src/app/models/data/address';
import { emptyUser, PhoneNumber } from 'src/app/models/data/user';
import {
	Address,
	BigIntId,
	Email,
	emptyOneDayCodeGen3,
	FirstName,
	LastName,
	OneDayCodeGen3,
	User,
	UtcDate,
	UtcDateTime,
} from 'src/app/models/data/oneDayCode';
import { emptyFlexCodeGen4, FlexCodeGen4 } from 'src/app/models/data/flexCode';
import { FeatureFlagName } from 'src/app/models/data/featureFlag';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { UserProfile } from 'src/app/models/model';
import { ApiService, QueryParam } from 'src/app/services/api.services';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { TimeZoneService } from 'src/app/services/timezone.service';
import { DateTime } from 'luxon';
import {
	FormControl,
	FormsModule,
	NgModel,
	ReactiveFormsModule,
	Validators,
} from '@angular/forms';
import {
	ControlAssignment,
	DeviceControl,
	emptyDeviceControl,
	PDQ_KMS_GENERATION_1,
	PDQ_SDS_GENERATION_1,
} from 'src/app/models/data/deviceControl';
import {
	MatTab,
	MatTabChangeEvent,
	MatTabGroup,
	MatTabLabel,
} from '@angular/material/tabs';
import { emptyHaspCode } from 'src/app/models/data/haspCode';
import { MOBILE_BREAKPOINT } from 'src/app/app.constants';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { DeviceSearchBarComponent } from 'src/app/components/device-search-bar/device-search-bar.component';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { NotAuthorizedNoticeComponent } from '../not-authorized-notice/not-authorized-notice.component';
import { DeviceGenerateCodeBoxComponent } from '../../ui/device/detail/device-generate-code/device-generate-code.component';
import { MatDivider } from '@angular/material/list';
import { MatChip, MatChipSet } from '@angular/material/chips';
import { DeviceSearchBarComponent as DeviceSearchBarComponent_1 } from '../device-search-bar/device-search-bar.component';
import { MatGridList, MatGridTile } from '@angular/material/grid-list';
import {
	MatError,
	MatFormField,
	MatLabel,
	MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {
	MatButtonToggle,
	MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import {
	MatDatepicker,
	MatDatepickerInput,
	MatDatepickerToggle,
	MatDatepickerToggleIcon,
} from '@angular/material/datepicker';
import { PhoneTypeFormComponent } from '../phones-form/phone-type-form.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTooltip } from '@angular/material/tooltip';
import { MilitaryTimePickerComponent } from '../military-time-picker/military-time-picker.component';
import { DebounceClickDirective } from '../../directives/debounce-click/debounce-click.directive';
import {
	AccessDatePlaceholderPipe,
	DeviceAccessControlDurationPipe,
	DeviceControlDaysPipe,
} from '../pipes';

type ControlResponse = {
	control: Components.Schemas.DeviceControl;
	assignments: Components.Schemas.ControlAssignment[];
	devices: Components.Schemas.Device[];
};

/** Max flex-code span the Generate Code API accepts (utcEndTime vs utcStartTime). */
export const MAX_FLEX_CODE_DURATION_DAYS = 30;

/**
 * Devices that support code generation:
 * 1. Gen 4 lockboxes - supports flex codes and hasp codes
 * 2. Gen 3 lockboxes - supports one day codes and hasp codes¹
 * 3. KMS lockset - supports flex codes
 * 4. KDS lockset - supports flex codes
 *
 * ¹ Wall mounts do not support hasp codes
 */
@Component({
	selector: 'app-device-generate-code-dialog',
	templateUrl: './device-generate-code-dialog.component.html',
	styleUrls: [
		'./device-generate-code-dialog.component.scss',
		'../../ui/common/main.scss',
	],
	providers: [DatePipe],
	imports: [
		MatIconButton,
		MatIcon,
		NotAuthorizedNoticeComponent,
		MatDialogContent,
		DeviceGenerateCodeBoxComponent,
		NgTemplateOutlet,
		MatDivider,
		MatChipSet,
		MatChip,
		DeviceSearchBarComponent_1,
		MatGridList,
		MatGridTile,
		MatTabGroup,
		MatTab,
		MatTabLabel,
		MatFormField,
		MatInput,
		CdkTextareaAutosize,
		FormsModule,
		MatButtonToggleGroup,
		MatButtonToggle,
		MatDatepickerInput,
		ReactiveFormsModule,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepickerToggleIcon,
		MatDatepicker,
		PhoneTypeFormComponent,
		MatCheckbox,
		MatTooltip,
		MatError,
		MilitaryTimePickerComponent,
		MatDialogActions,
		DebounceClickDirective,
		NgStyle,
		DeviceControlDaysPipe,
		DeviceAccessControlDurationPipe,
		AccessDatePlaceholderPipe,
		MatLabel,
	],
})
export class DeviceGenerateCodeDialogComponent
	implements
		OnInit,
		AfterContentInit,
		AfterViewInit,
		AfterViewChecked,
		OnDestroy
{
	private static readonly CODE_GEN_TAB_AUTOMATION_IDS = [
		'access-code-tab',
		'hasp-code-tab',
	] as const;
	oneDayCodeGen3Request: OneDayCodeGen3;
	flexCodeGen4Request: FlexCodeGen4;
	oneDayOrFlexCodeEdits: OneDayOrFlexCodeGen;
	notifyByEmail = false;
	includeInEmail: {
		address: boolean;
		propertyName: boolean;
		deviceLocation: boolean;
	};
	notifyBySms = false;
	includeInSms: {
		address: boolean;
		propertyName: boolean;
		deviceLocation: boolean;
	};

	PDQ_KMS_GENERATION_1 = PDQ_KMS_GENERATION_1;
	PDQ_SDS_GENERATION_1 = PDQ_SDS_GENERATION_1;
	isPDQDevice: boolean = false;

	_device: Device = emptyDevice();
	get device(): Device {
		return this._device;
	}
	set device(device: Device) {
		this._device = device;
		this.isGen4Device =
			this.device?.deviceGeneration?.toLowerCase()?.includes('gen4') ?? false;
		this.isWallMountDevice =
			this.device?.deviceType?.toLowerCase()?.includes('wall mount') ?? false;
		this.isPDQDevice =
			this.device?.deviceGeneration === PDQ_KMS_GENERATION_1 ||
			this.device?.deviceGeneration === PDQ_SDS_GENERATION_1;
	}

	userProfile: UserProfile | undefined;
	selectedDeviceForSync: Device | null = null;

	canIssueFlexCodes: boolean;
	fromDeviceScreen = false;

	disableCodeGenerateButton = true;
	codeSuccessfullyGenerated = false;
	/** Present when the create response includes a Companion for a listed serial. */
	companion?: OneDayCodeGen3;
	/** Server clock for Until / After. Empty when the response omits it. */
	companionWindowStartLabel?: string;

	minStartDate: Date = new Date();
	maxStartDate: Date = new Date();
	minEndDate: Date = new Date();
	maxEndDate: Date = new Date();

	startTimeMustBeAfter?: string;
	startTimeMustBeBefore?: string;
	endTimeMustBeAfter?: string;
	endTimeMustBeBefore?: string;

	activeCompanyId: string | undefined = '';
	companyTimezone?: string | undefined = '';
	currentDateinLocationTimezone?: string;
	currentTimeinLocationTimezone?: string;
	locationTimezone?: string;
	offset?: string;

	disabledPhoneToggle: boolean = true;
	disabledEmailToggle: boolean = true;

	isGen4Device: boolean = false;
	isDeviceOwner: boolean = false;
	isWallMountDevice: boolean = false;

	mobilePhone: PhoneNumber = {};

	nonOverlappingTooltip = $localize`:@@deviceGenerateOneDayCode-NonOverlappingTooltip:Using new code disables previously entered non-overlapping codes`;

	@ViewChild('serialSearchBar') serialSearchBar!: DeviceSearchBarComponent;
	@ViewChild('emailToggle') emailToggle!: NgModel;
	@ViewChild('phoneToggle') phoneToggle!: NgModel;
	dateTimeControl = new FormControl('', [Validators.required]);
	endDateTimeControl = new FormControl('', [Validators.required]);
	codeGenTabIndex: number = 0;
	private tabAutomationIdsAdded = false;

	@Input() fetchingDeviceControlSummaries = false;
	@Input() resultingDeviceAccess?: {
		control: DeviceControl;
		assignments: ControlAssignment[];
		devices: Device[];
	}[] = undefined;
	selectedDeviceAccess: DeviceControl = emptyDeviceControl();
	selectedDeviceAssignments?: ControlAssignment[];
	hasInheritedAccess: boolean = false;

	allowAccessCodeGen: boolean = false;
	allowHaspCodeGen: boolean = false;
	isHaspCodeGenForm: boolean = false;
	willRemoveFromProperty: boolean = true;
	isHaspCodeGenFeatureEnabled: boolean = false;
	// Fetch or Check the device is Sentri-Vault then its "True" otherwise "False" :
	isSentriVaultDeviceFlag: boolean = false;

	selectedFilterOption: 'serialNumber' | 'propertyName' | 'deviceLocation' =
		'serialNumber';

	// Translation variables for placeholders
	readonly firstNamePlaceholder = $localize`:@@deviceGenerateOneDayCode-firstName:First Name`;
	readonly lastNamePlaceholder = $localize`:@@deviceGenerateOneDayCode-lastName:Last Name`;
	readonly emailPlaceholder = $localize`:@@deviceGenerateOneDayCode-email:Email Address`;
	readonly reasonPlaceholder = $localize`:@@deviceGenerateOneDayCode-reasonForGenerationTitle:Reason for Generation`;
	readonly newLocationPlaceholder = $localize`:@@deviceGenerateOneDayCode-newLocation:New Location`;
	readonly dateValidPlaceholder = $localize`:@@deviceGenerateOneDayCode-dateValidTitle:Date Valid`;
	readonly startDatePlaceholder = $localize`:@@deviceGenerateOneDayCode-startDateTitle:Start Date`;
	readonly endDatePlaceholder = $localize`:@@deviceGenerateOneDayCode-endDateTitle:End Date`;
	readonly startTimePlaceholder = $localize`:@@deviceGenerateOneDayCode-startTimeTitle:Start Time`;
	readonly endTimePlaceholder = $localize`:@@deviceGenerateOneDayCode-endTimeTitle:End Time`;
	readonly optionalPlaceholder = $localize`:@@deviceGenerateOneDayCode-optional:Optional`;
	readonly deviceLookupInstructionsPlaceholder = $localize`:@@deviceGenerateOneDayCode-deviceLookupInstructionsPlaceholder:Find your device and generate an access code. Search by serial number, property name, or device location.`;
	readonly serialNumberPlaceholder = $localize`:@@deviceGenerateOneDayCode-serialNumberPlaceholder:Serial Number`;
	readonly propertyNamePlaceholder = $localize`:@@deviceGenerateOneDayCode-propertyNamePlaceholder:Property Name`;
	readonly deviceLocationPlaceholder = $localize`:@@deviceGenerateOneDayCode-deviceLocationPlaceholder:Device Location`;

	defaultGridRowHeight = '52px';
	gridRowHeight = this.defaultGridRowHeight;
	private destroy$ = new Subject<void>();

	constructor(
		@Inject(MAT_DIALOG_DATA)
		public data: {
			deviceId: string | null;
			userProfile: UserProfile | null;
			device: Device | null;
			fromDeviceScreen: boolean;
		},
		@Inject(LOCALE_ID) private locale: string,
		private readonly dialogRef: MatDialogRef<DeviceGenerateCodeDialogComponent>,
		private readonly apiService: ApiService,
		private readonly scService: SystemCapabilityService,
		private readonly snackbar: MatSnackBar,
		readonly datePipe: DatePipe,
		readonly timeZoneService: TimeZoneService,
		private breakpointObserver: BreakpointObserver
	) {
		if (data.device) {
			this.device = data.device;
			this.companyTimezone =
				this.device.activeDeviceAssignment?.activeInstallation
					?.installationLocation?.location?.timezone;
			this.isSentriVaultDeviceFlag = isSentriVaultDevice(this.device) ?? false;
		}

		this.activeCompanyId = this.data.userProfile?.activeRoleDetails?.company.id;

		// we need to set the country code on PhoneNumber object to be passed into the phone-type-form
		this.mobilePhone = {
			countryCode: this.getCountryCodeDefault(),
		};

		this.fromDeviceScreen = data.fromDeviceScreen;

		// Pass dialogCloseResult on backdrop/Escape so tab afterClosed can refresh
		this.dialogRef.disableClose = true;
		this.dialogRef
			.backdropClick()
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => this.onCloseClicked());
		this.dialogRef
			.keydownEvents()
			.pipe(
				filter((event) => event.key === 'Escape'),
				takeUntil(this.destroy$)
			)
			.subscribe(() => this.onCloseClicked());

		this.oneDayCodeGen3Request = emptyOneDayCodeGen3();
		this.flexCodeGen4Request = emptyFlexCodeGen4();
		this.oneDayOrFlexCodeEdits = emptyCodeGenFields();
		this.includeInEmail = {
			address: false,
			propertyName: false,
			deviceLocation: false,
		};
		this.includeInSms = {
			address: false,
			propertyName: false,
			deviceLocation: false,
		};

		if (data.deviceId) {
			this.oneDayOrFlexCodeEdits.deviceId = data.deviceId;
		}

		if (data.userProfile) {
			this.userProfile = data.userProfile;
			this.setCreatorFields(data.userProfile);
		}

		this.canIssueFlexCodes = this.scService.has(
			SystemCapabilityEnum.issueFlexCodes
		);
		this.isDeviceOwner =
			data.device?.activeDeviceAssignment?.companyId ===
			this.userProfile?.activeRoleDetails?.company.id;
		if (
			(data.deviceId || data.device?.id) &&
			this.canIssueFlexCodes &&
			!this.isDeviceOwner
		) {
			// check if the device is inherited, whether or not they have access to generate codes
			this.fetchingDeviceControlSummaries = true;
			this.hasInheritedAccess = true;
			this.requestDeviceControlSummaries();
		}
	}

	async ngOnInit(): Promise<void> {
		this.isHaspCodeGenFeatureEnabled = (
			await this.apiService.getFeatureFlagByName(FeatureFlagName.webHaspCodes)
		)?.value;

		if (!this.oneDayOrFlexCodeEdits.creatorId) {
			this.userProfile = await this.apiService.getUserProfile();
			this.setCreatorFields(this.userProfile);
		}

		this.breakpointObserver
			.observe([`(max-width: ${MOBILE_BREAKPOINT}px)`])
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				this.gridRowHeight = result.matches
					? '76px'
					: this.defaultGridRowHeight;
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	respondToVisibility = function (element: any, callback: any) {
		var options = {
			root: document.documentElement,
		};

		var observer = new IntersectionObserver((entries, observer) => {
			entries.forEach((entry) => {
				callback(entry.intersectionRatio > 0);
			});
		}, options);

		observer.observe(element);
	};

	ngAfterContentInit() {
		this.updateEmailToggleCheckboxes();
		this.updatePhoneToggleCheckboxes();
	}

	async ngAfterViewInit(): Promise<void> {
		if (this.device?.id && !this.device?.codeGenerationAutofill) {
			try {
				this.device = await this.getDeviceById(this.device.id);
				this.isSentriVaultDeviceFlag =
					isSentriVaultDevice(this.device) ?? false;
			} catch {
				console.error('Can not get device details');
			}
		}

		if (!this.companyTimezone && this.activeCompanyId) {
			const response = await this.apiService.getCompanyById(
				this.activeCompanyId
			);
			this.companyTimezone =
				response.timezone?.name || response.timezone?.timezone;
		}
		this.setTimeDateInLocationTimezone();
		this.applyDefaultDateValues();
	}

	ngAfterViewChecked(): void {
		if (!this.tabAutomationIdsAdded) {
			this.addStaticAutomationIdsToTabs();
		}
	}

	private resetTabAutomationIds(): void {
		this.tabAutomationIdsAdded = false;
	}

	addStaticAutomationIdsToTabs(): void {
		const codeTabs = document.getElementById('code-tabs');
		if (!codeTabs) {
			return;
		}

		const tabHeaders = codeTabs.getElementsByClassName('mat-mdc-tab');
		if (
			tabHeaders.length <
			DeviceGenerateCodeDialogComponent.CODE_GEN_TAB_AUTOMATION_IDS.length
		) {
			return;
		}

		for (let i = 0; i < tabHeaders.length; i++) {
			const automationId =
				DeviceGenerateCodeDialogComponent.CODE_GEN_TAB_AUTOMATION_IDS[i];
			if (automationId) {
				tabHeaders[i].setAttribute('data-automation-id', automationId);
			}
		}

		this.tabAutomationIdsAdded = true;
	}

	setTimeDateInLocationTimezone(): void {
		// subtract 29 minutes from current time
		// this is to handle situation so the flex codes can be generated to be used right away and users do not have to wait
		// if current time 8:45 am, user should be allowed to select 8:30 as the start time
		const utcString = new Date(Date.now() - 60000 * 29).toISOString();
		this.locationTimezone =
			this.device?.activeDeviceAssignment?.activeInstallation
				?.installationLocation?.location?.timezone ||
			this.companyTimezone ||
			'UTC';
		this.offset = this.timeZoneService.utcOffset(
			utcString,
			this.locationTimezone
		);
		this.currentTimeinLocationTimezone =
			this.datePipe.transform(utcString, 'HH:mm', this.offset) ?? '';
		this.currentDateinLocationTimezone =
			this.datePipe.transform(utcString, 'yyyy-MM-dd', this.offset) ?? '';
	}

	private parseLocalDate(localDate: string): Date {
		const [year, month, day] = localDate.split('-').map(Number);
		return new Date(year, month - 1, day);
	}

	private isInheritedFullDayAccessWindow(): boolean {
		return (
			this.selectedDeviceAccess?.startTime === '00:00:00' &&
			this.selectedDeviceAccess?.endTime === '24:00:00'
		);
	}

	private constrainEndDateBoundsForAccessWindow(startDate: Date): void {
		this.minEndDate = startDate;
		if (!this.hasInheritedAccess) {
			this.maxEndDate = this.addDays(startDate, MAX_FLEX_CODE_DURATION_DAYS);
			return;
		}
		if (!this.isInheritedFullDayAccessWindow()) {
			this.maxEndDate = startDate;
			return;
		}
		let currentDate = new Date(startDate);
		while (currentDate <= this.maxEndDate) {
			if (!this.canSelectDay(currentDate, true)) {
				this.maxEndDate = currentDate;
				break;
			}
			currentDate.setDate(currentDate.getDate() + 1);
		}
	}

	private normalizeTimeForPicker(time?: string): string | undefined {
		if (!time) {
			return undefined;
		}

		if (time.length === 5) {
			return `${time}:00`;
		}

		return time;
	}

	private getCurrentHalfHourInLocationTimezone(): string {
		const nowInTimezone = DateTime.now().setZone(
			this.locationTimezone || 'UTC'
		);
		const flooredMinutes = nowInTimezone.minute < 30 ? 0 : 30;

		return nowInTimezone
			.set({ minute: flooredMinutes, second: 0, millisecond: 0 })
			.toFormat('HH:mm:ss');
	}

	private subtractThirtyMinutes(time: string): string {
		const parsedTime = DateTime.fromFormat(time, 'HH:mm:ss', { zone: 'UTC' });
		return parsedTime.minus({ minutes: 30 }).toFormat('HH:mm:ss');
	}

	private resolveDefaultLocalDate(): string {
		const localDate =
			this.device.codeGenerationAutofill?.localDate ||
			this.currentDateinLocationTimezone;
		if (!localDate) {
			return '';
		}

		const parsedLocalDate = this.parseLocalDate(localDate);
		if (this.checkValidDateBoundsOfInheritanceRules(parsedLocalDate)) {
			return localDate;
		}

		if (this.selectedDeviceAssignments?.length) {
			const assignmentStartDate = new Date(
				this.selectedDeviceAssignments[0].utcStartDate
			);
			const minAllowedDate =
				assignmentStartDate > new Date() ? assignmentStartDate : new Date();
			return formatDate(minAllowedDate, 'yyyy-MM-dd', this.locale);
		}

		return localDate;
	}

	private resolveFallbackStartTime(): string {
		let suggestedStartTime = this.getCurrentHalfHourInLocationTimezone();
		const controlStartTime = this.selectedDeviceAccess.startTime;
		const controlEndTime = this.selectedDeviceAccess.endTime;

		if (
			this.hasInheritedAccess &&
			controlStartTime &&
			suggestedStartTime < controlStartTime
		) {
			suggestedStartTime = controlStartTime;
		}

		if (
			this.hasInheritedAccess &&
			controlEndTime &&
			controlEndTime !== '24:00:00'
		) {
			const latestPossibleStartTime =
				this.subtractThirtyMinutes(controlEndTime);
			if (suggestedStartTime > latestPossibleStartTime) {
				suggestedStartTime = latestPossibleStartTime;
			}
		}

		return suggestedStartTime;
	}

	private resolveFallbackEndTime(): string {
		if (this.hasInheritedAccess && this.selectedDeviceAccess.endTime) {
			return this.selectedDeviceAccess.endTime;
		}

		return '24:00:00';
	}

	private applySingleDayDefault(localDate: string): void {
		if (!localDate) {
			return;
		}

		this.oneDayOrFlexCodeEdits.utcValidDate = localDate;
		this.dateTimeControl.setValue(this.parseLocalDate(localDate) as never);
	}

	private patchCodeGenerationAutofillTimes(): void {
		if (!this.device?.codeGenerationAutofill) {
			return;
		}

		this.startTimeMustBeBefore = this.device?.codeGenerationAutofill
			?.accessWindowLocalEndTime as string;
		this.oneDayOrFlexCodeEdits.startTime = this.device?.codeGenerationAutofill
			?.suggestedStartTime as string;

		this.endTimeMustBeBefore = this.device?.codeGenerationAutofill
			?.accessWindowLocalEndTime as string;
		this.oneDayOrFlexCodeEdits.endTime = this.device?.codeGenerationAutofill
			?.suggestedEndTime as string;
	}

	private applyFlexDefaults(localDate: string): void {
		if (!localDate) {
			return;
		}

		const autofill = this.device.codeGenerationAutofill;
		const suggestedStartTime =
			this.normalizeTimeForPicker(autofill?.suggestedStartTime) ||
			this.resolveFallbackStartTime();
		const suggestedEndTime =
			this.normalizeTimeForPicker(autofill?.suggestedEndTime) ||
			this.resolveFallbackEndTime();

		this.oneDayOrFlexCodeEdits.startDate = localDate;
		this.oneDayOrFlexCodeEdits.endDate = localDate;
		this.oneDayOrFlexCodeEdits.startTime = suggestedStartTime;
		this.oneDayOrFlexCodeEdits.endTime = suggestedEndTime;
		this.oneDayOrFlexCodeEdits.utcCreateDate = new Date().toISOString();

		const parsedLocalDate = this.parseLocalDate(localDate);
		this.dateTimeControl.setValue(parsedLocalDate as never);
		this.constrainEndDateBoundsForAccessWindow(parsedLocalDate);
		this.endDateTimeControl.setValue(parsedLocalDate as never);

		this.patchCodeGenerationAutofillTimes();
		this.setEndTimeMustBeAfter();
	}

	private applyDefaultDateValues(): void {
		if (!this.device?.id) {
			return;
		}

		const localDate = this.resolveDefaultLocalDate();
		if (!localDate) {
			return;
		}

		if (this.showStartAndEndDateTimeframe() && !this.isHaspCodeGenForm) {
			this.applyFlexDefaults(localDate);
		} else {
			this.applySingleDayDefault(localDate);
		}

		this.isOneDayCodeDisabled();
	}

	onCloseClicked(): void {
		this.dialogRef.close(this.dialogCloseResult());
	}

	updateUserPhone(mobilePhone: PhoneNumber): void {
		this.oneDayOrFlexCodeEdits.mobilePhoneNumber = mobilePhone.rawPhoneNumber;
		this.isOneDayCodeDisabled();
		this.updatePhoneToggleCheckboxes();
	}

	// For get the flag for require reason for code generation :
	get reasonRequireForGenerationReasonRequired(): boolean {
		return this.device?.requireReasonForCodeGeneration !== false;
	}

	async getDeviceById(deviceId: string): Promise<Device> {
		return await this.apiService.getDeviceById(deviceId);
	}

	setCreatorFields(userProfile: UserProfile): void {
		this.oneDayOrFlexCodeEdits.creatorId = userProfile.user.id;
		this.oneDayOrFlexCodeEdits.creatorCompanyId =
			userProfile.activeRoleDetails?.company.id ?? '';
		this.oneDayOrFlexCodeEdits.creator = userProfile.user;
	}

	isOneDayCodeDisabled(): void {
		const reasonValid =
			!this.reasonRequireForGenerationReasonRequired ||
			this.oneDayOrFlexCodeEdits.reason.trim() !== '';

		if (
			this.device?.deviceGeneration?.toLowerCase()?.includes('gen3') &&
			reasonValid &&
			this.oneDayOrFlexCodeEdits.utcValidDate !== '' &&
			this.oneDayOrFlexCodeEdits.firstName !== '' &&
			this.oneDayOrFlexCodeEdits.lastName !== '' &&
			this.device?.id &&
			!(this.isWallMountDevice && this.isHaspCodeGenForm)
		) {
			this.disableCodeGenerateButton = false;
		} else if (
			this.device?.deviceGeneration?.toLowerCase()?.includes('gen4') ||
			this.isPDQDevice
		) {
			const todayDate = formatDate(Date(), 'yyyy-MM-dd', this.locale);
			if (
				this.oneDayOrFlexCodeEdits.firstName !== '' &&
				this.oneDayOrFlexCodeEdits.lastName !== '' &&
				reasonValid &&
				this.device?.id
			) {
				if (
					this.isHaspCodeGenForm &&
					this.oneDayOrFlexCodeEdits.utcValidDate !== '' &&
					this.oneDayOrFlexCodeEdits.utcValidDate >= todayDate &&
					!this.isWallMountDevice
				) {
					this.disableCodeGenerateButton = false;
				} else if (
					!this.isHaspCodeGenForm &&
					this.oneDayOrFlexCodeEdits.startDate !== '' &&
					this.oneDayOrFlexCodeEdits.startDate >= todayDate &&
					this.oneDayOrFlexCodeEdits.endDate !== '' &&
					this.oneDayOrFlexCodeEdits.endDate >= todayDate &&
					this.oneDayOrFlexCodeEdits.startTime !== '' &&
					this.oneDayOrFlexCodeEdits.endTime !== '' &&
					new Date(this.oneDayOrFlexCodeEdits.startDate) <=
						new Date(this.oneDayOrFlexCodeEdits.endDate) &&
					this.dateTimeControl.valid &&
					this.endDateTimeControl.valid
				) {
					this.disableCodeGenerateButton = false;
				} else {
					this.disableCodeGenerateButton = true;
				}
			} else {
				this.disableCodeGenerateButton = true;
			}
		} else {
			this.disableCodeGenerateButton = true;
		}

		// If the date control isn't valid don't allow the user to submit
		setTimeout(() => {
			if (
				!this.dateTimeControl.valid ||
				(!this.isHaspCodeGenForm &&
					this.device?.deviceGeneration?.toLowerCase()?.includes('gen4') &&
					!this.endDateTimeControl.valid)
			) {
				this.disableCodeGenerateButton = true;
			}
		}, 0);
	}

	updateEmailToggleCheckboxes(): void {
		const email = this.oneDayOrFlexCodeEdits.email;
		if (email ? !!email.trim() : false) {
			if (this.device?.id) {
				this.disabledEmailToggle = false;
				this.emailToggle?.control.setValue(true);
				// US12345: Always sending out this information if user includes email
				this.notifyByEmail = true;
				this.includeInEmail = {
					address: true,
					propertyName: true,
					deviceLocation: true,
				};
			}
		} else {
			this.disabledEmailToggle = true;
			this.emailToggle?.control.setValue(false);
			this.includeInEmail = {
				address: false,
				propertyName: false,
				deviceLocation: false,
			};
		}
	}

	updatePhoneToggleCheckboxes(): void {
		const mobilePhoneNumber = this.oneDayOrFlexCodeEdits.mobilePhoneNumber;
		// added the check for phone number > 3 to account for phone country code (some countries have 3 digit country code like IE)
		// this was needed for the Generate Code button in the main menu
		if (
			mobilePhoneNumber
				? !!mobilePhoneNumber.trim() && mobilePhoneNumber.length > 3
				: false
		) {
			if (this.device?.id) {
				this.disabledPhoneToggle = false;
				this.phoneToggle?.control.setValue(true);
				// US12345: Always sending out this information if user includes mobile number
				this.notifyBySms = true;
				this.includeInSms = {
					address: true,
					propertyName: true,
					deviceLocation: true,
				};
			}
		} else {
			this.disabledPhoneToggle = true;
			this.phoneToggle?.control.setValue(false);
			this.includeInSms = {
				address: false,
				propertyName: false,
				deviceLocation: false,
			};
		}
	}

	getCountryCodeDefault(): string | undefined {
		const propertyCountryCode =
			this.device.activeDeviceAssignment?.activeInstallation
				?.installationLocation?.location?.address.countryCode;
		const companyCountryCode =
			this.device.activeDeviceAssignment?.company?.countryCode;
		return (
			propertyCountryCode?.toLocaleLowerCase() ??
			companyCountryCode?.toLocaleLowerCase()
		);
	}

	wasConfirmationSent(): boolean {
		const mobilePhoneNumber =
			this.oneDayOrFlexCodeEdits.mobilePhoneNumber ?? '';
		const email = this.oneDayOrFlexCodeEdits.email ?? '';
		const wasPhoneConfirmationSent = mobilePhoneNumber.length > 0;
		const wasEmailConfirmationSent = email.length > 0;

		return wasEmailConfirmationSent || wasPhoneConfirmationSent;
	}

	addDays(date: Date, days: number): Date {
		const copy = new Date(Number(date));
		copy.setDate(date.getDate() + days);
		return copy;
	}

	transformValidDate(date: Date): void {
		if (!this.checkValidDateBoundsOfInheritanceRules(date)) {
			return;
		}
		this.oneDayOrFlexCodeEdits.utcValidDate = formatDate(
			date,
			'yyyy-MM-dd',
			this.locale
		);
		this.oneDayOrFlexCodeEdits.utcCreateDate = new Date().toISOString();
		this.isOneDayCodeDisabled();
	}

	transformStartDate(date: Date): void {
		if (!this.checkValidDateBoundsOfInheritanceRules(date)) {
			return;
		}
		this.oneDayOrFlexCodeEdits.startDate = formatDate(
			date,
			'yyyy-MM-dd',
			this.locale
		);
		this.oneDayOrFlexCodeEdits.utcCreateDate = new Date().toISOString();
		this.constrainEndDateBoundsForAccessWindow(date);
		this.setEndTimeMustBeAfter();
		// if start date is selected and end date is set, reset it to make sure end date is after start date
		if (this.oneDayOrFlexCodeEdits.endDate !== '') {
			this.oneDayOrFlexCodeEdits.endDate = '';
			this.resetEndDateControl();
		}
	}

	formatControlName(deviceAccessControl: DeviceControl): string {
		return deviceAccessControl.companyName ?? '';
	}

	canSelectDay(day: Date, checkNextDay: boolean): boolean {
		let checkDay: any;
		if (checkNextDay) {
			// Clone the given date and add 1 day
			const nextDay = new Date(day);
			nextDay.setDate(nextDay.getDate() + 1);
			checkDay = nextDay;
		} else {
			checkDay = day;
		}

		if (!this.checkValidDateBoundsOfInheritanceRules(checkDay)) {
			return false;
		}

		// check if the nextDay is disabled
		if (
			checkDay.getDay() === 1 &&
			!this.selectedDeviceAccess.days.monday.value
		) {
			return false;
		}
		if (
			checkDay.getDay() === 2 &&
			!this.selectedDeviceAccess.days.tuesday.value
		) {
			return false;
		}
		if (
			checkDay.getDay() === 3 &&
			!this.selectedDeviceAccess.days.wednesday.value
		) {
			return false;
		}
		if (
			checkDay.getDay() === 4 &&
			!this.selectedDeviceAccess.days.thursday.value
		) {
			return false;
		}
		if (
			checkDay.getDay() === 5 &&
			!this.selectedDeviceAccess.days.friday.value
		) {
			return false;
		}
		if (
			checkDay.getDay() === 6 &&
			!this.selectedDeviceAccess.days.saturday.value
		) {
			return false;
		}
		if (
			checkDay.getDay() === 0 &&
			!this.selectedDeviceAccess.days.sunday.value
		) {
			return false;
		}

		return true;
	}

	checkValidDateBoundsOfInheritanceRules(date: Date): boolean {
		if (!date) return false;

		// check if the day is within bounds of inheritance assignments (if applicable)
		if (this.selectedDeviceAssignments) {
			const minDate = new Date(
				(new Date(this.selectedDeviceAssignments[0].utcStartDate) < new Date()
					? new Date()
					: new Date(this.selectedDeviceAssignments[0].utcStartDate)
				).setHours(0, 0, 0, 0)
			);
			const maxDate = new Date(
				new Date(
					this.selectedDeviceAssignments[0].utcEndDate ?? '12/31/2500'
				).setHours(0, 0, 0, 0)
			);
			date = new Date(date.setHours(0, 0, 0, 0));

			if (minDate > date || maxDate < date) {
				return false;
			}
		}
		return true;
	}

	transformEndDate(date: Date): void {
		if (!this.checkValidDateBoundsOfInheritanceRules(date)) {
			return;
		}
		if (
			this.hasInheritedAccess &&
			!this.isInheritedFullDayAccessWindow() &&
			this.oneDayOrFlexCodeEdits.startDate &&
			formatDate(date, 'yyyy-MM-dd', this.locale) !==
				this.oneDayOrFlexCodeEdits.startDate
		) {
			return;
		}
		this.oneDayOrFlexCodeEdits.endDate = formatDate(
			date,
			'yyyy-MM-dd',
			this.locale
		);
		this.oneDayOrFlexCodeEdits.utcCreateDate = new Date().toISOString();
		this.setEndTimeMustBeAfter();
	}

	onStartTimeSelected(startTime: string): void {
		this.oneDayOrFlexCodeEdits.startTime = startTime;
		this.setEndTimeMustBeAfter();
	}

	onEndTimeSelected(endTime: string): void {
		this.oneDayOrFlexCodeEdits.endTime = endTime;
		this.isOneDayCodeDisabled(); // adding this to enable the button if user isn't following the sequence of inputs on the screen
	}

	setEndTimeMustBeAfter(): void {
		// if start date is today, then disable the time before the currentDateinLocationTimezone so a code isn't generated for past times
		if (this.checkIsStartDateToday()) {
			this.startTimeMustBeAfter = this.currentTimeinLocationTimezone;
			if (
				!!this.startTimeMustBeAfter &&
				this.startTimeMustBeAfter !== '' &&
				this.oneDayOrFlexCodeEdits.startTime !== '' &&
				this.oneDayOrFlexCodeEdits.startTime <= this.startTimeMustBeAfter
			) {
				this.oneDayOrFlexCodeEdits.startTime = '';
				this.patchCodeGenerationAutofillTimes();
			}
		}

		if (this.hasInheritedAccess && this.selectedDeviceAccess.startTime) {
			if (this.selectedDeviceAccess.startTime !== '00:00:00') {
				// subtract 30 min so if they were give access from 2:30 that is an option to select
				let currentTimeString = this.selectedDeviceAccess.startTime;
				let currentDateTime = new Date(`01/01/2000 ${currentTimeString}`);
				// subtract 30 min
				currentDateTime.setMinutes(currentDateTime.getMinutes() - 30);
				let updatedTimeString = currentDateTime.toLocaleTimeString([], {
					hour: 'numeric',
					minute: '2-digit',
					hour12: false,
				});
				updatedTimeString = updatedTimeString + ':00'; // append :00 at the end
				if (updatedTimeString.length === 5) {
					// account for non-12 hour time format
					updatedTimeString = updatedTimeString + ':00';
				} else if (updatedTimeString.length !== 8) {
					// the hour was returned a 1 digit so append 0 in the front (6:30:00 instead of 06:30:00)
					updatedTimeString = '0' + updatedTimeString;
				}
				// check if the date is today and the start time was already set
				if (
					!this.startTimeMustBeAfter ||
					!this.checkIsStartDateToday() ||
					(this.checkIsStartDateToday() &&
						this.isBefore(this.startTimeMustBeAfter, updatedTimeString))
				) {
					this.startTimeMustBeAfter = updatedTimeString;
				}
			} else if (!this.checkIsStartDateToday()) {
				// reset this so 00:00:00 can be selected
				this.startTimeMustBeAfter = undefined;
			}
		}

		// If neither of the above 2 conditions are met
		if (
			!this.checkIsStartDateToday() &&
			!(this.hasInheritedAccess && this.selectedDeviceAccess.startTime)
		) {
			this.startTimeMustBeAfter = undefined;
		}

		// if the start and end date is the same then the end time must be after the start time
		if (
			this.oneDayOrFlexCodeEdits.startDate &&
			this.oneDayOrFlexCodeEdits.startDate ===
				this.oneDayOrFlexCodeEdits.endDate &&
			this.oneDayOrFlexCodeEdits.startTime
		) {
			this.endTimeMustBeAfter = this.oneDayOrFlexCodeEdits.startTime;

			if (
				!!this.endTimeMustBeAfter &&
				this.endTimeMustBeAfter !== '' &&
				this.oneDayOrFlexCodeEdits.endTime <= this.endTimeMustBeAfter
			) {
				this.oneDayOrFlexCodeEdits.endTime = '';
				this.patchCodeGenerationAutofillTimes();
			}
		} else if (this.hasInheritedAccess) {
			if (
				!this.selectedDeviceAccess.endTime ||
				this.selectedDeviceAccess.startTime === '00:00:00'
			) {
				// Full-day inherited windows: do not keep a same-day start-time floor on end time
				this.endTimeMustBeAfter = undefined;
			} else if (this.selectedDeviceAccess.startTime !== '00:00:00') {
				this.endTimeMustBeAfter = this.startTimeMustBeAfter;
			}
		} else {
			// Multi-day owner codes: do not keep a same-day start-time floor on end time
			this.endTimeMustBeAfter = undefined;
		}
		this.isOneDayCodeDisabled();
	}

	generateHaspCode(): void {
		const haspCodeEdits = emptyHaspCode();
		haspCodeEdits.id = this.oneDayOrFlexCodeEdits.id;
		haspCodeEdits.deviceId = this.oneDayOrFlexCodeEdits.deviceId;
		haspCodeEdits.device = this.oneDayOrFlexCodeEdits.device;
		haspCodeEdits.reason = this.oneDayOrFlexCodeEdits.reason;
		haspCodeEdits.creatorId = this.oneDayOrFlexCodeEdits.creatorId;
		haspCodeEdits.creatorCompanyId =
			this.oneDayOrFlexCodeEdits.creatorCompanyId;
		haspCodeEdits.utcCreateDate = this.oneDayOrFlexCodeEdits.utcCreateDate;
		haspCodeEdits.utcValidDate = this.oneDayOrFlexCodeEdits.utcValidDate;
		haspCodeEdits.firstName = this.oneDayOrFlexCodeEdits.firstName;
		haspCodeEdits.lastName = this.oneDayOrFlexCodeEdits.lastName;
		haspCodeEdits.email = this.oneDayOrFlexCodeEdits.email;
		haspCodeEdits.mobilePhoneNumber =
			this.oneDayOrFlexCodeEdits.mobilePhoneNumber;
		haspCodeEdits.propertyName =
			this.device.activeDeviceAssignment?.activeInstallation
				?.installationLocation?.location?.name;
		haspCodeEdits.deviceLocation =
			this.device.activeDeviceAssignment?.activeInstallation
				?.installationLocation?.name;
		haspCodeEdits.address =
			this.device.activeDeviceAssignment?.activeInstallation
				?.installationLocation?.location?.address;
		haspCodeEdits.removeDevice = this.willRemoveFromProperty;
		haspCodeEdits.newLocation = this.oneDayOrFlexCodeEdits.newLocation;
		// Pass the controlAssignmentId if user has inherited access and selected an assignment
		if (
			this.hasInheritedAccess &&
			this.selectedDeviceAssignments &&
			this.selectedDeviceAssignments.length > 0
		) {
			haspCodeEdits.controlAssignmentId = this.selectedDeviceAssignments[0].id;
		}

		this.apiService
			.requestAndSaveHaspCode(haspCodeEdits.deviceId, haspCodeEdits)
			.then((haspCode) => {
				this.snackbar.open(
					$localize`:@@genereateCode-codeGenerated:Successfully generated HaspCode.`,
					undefined,
					{
						duration: 2000,
					}
				);
				this.oneDayOrFlexCodeEdits.oneDayOrFlexCode = haspCode.haspCode;
				this.codeSuccessfullyGenerated = true;
			})
			.catch((_) => {
				this.snackbar.open(
					$localize`:@@generateCode-haspCodeGenerationError:There was a problem generating your HaspCode. Please try again.`,
					undefined,
					{
						duration: 2000,
					}
				);
			});
	}

	generateAccessCode(): void {
		if (
			this.device?.deviceGeneration?.toLowerCase()?.includes('gen4') ||
			this.isPDQDevice
		) {
			const flexCodeEdits = emptyFlexCodeGen4();
			flexCodeEdits.id = this.oneDayOrFlexCodeEdits.id;
			flexCodeEdits.deviceId = this.oneDayOrFlexCodeEdits.deviceId;
			flexCodeEdits.flexCode = this.oneDayOrFlexCodeEdits.oneDayOrFlexCode;
			flexCodeEdits.reason = this.oneDayOrFlexCodeEdits.reason;
			flexCodeEdits.creatorId = this.oneDayOrFlexCodeEdits.creatorId;
			flexCodeEdits.creatorCompanyId =
				this.oneDayOrFlexCodeEdits.creatorCompanyId;
			flexCodeEdits.utcCreateDate = this.oneDayOrFlexCodeEdits.utcCreateDate;
			flexCodeEdits.firstName = this.oneDayOrFlexCodeEdits.firstName;
			flexCodeEdits.lastName = this.oneDayOrFlexCodeEdits.lastName;
			flexCodeEdits.email = this.oneDayOrFlexCodeEdits.email;
			flexCodeEdits.mobilePhoneNumber =
				this.oneDayOrFlexCodeEdits.mobilePhoneNumber;
			flexCodeEdits.propertyName = this.oneDayOrFlexCodeEdits.propertyName;
			flexCodeEdits.deviceLocation = this.oneDayOrFlexCodeEdits.deviceLocation;
			flexCodeEdits.address = this.oneDayOrFlexCodeEdits.address;
			flexCodeEdits.locked = this.oneDayOrFlexCodeEdits.locked;
			flexCodeEdits.userLocalStartDate = this.oneDayOrFlexCodeEdits.startDate;
			flexCodeEdits.userLocalEndDate = this.oneDayOrFlexCodeEdits.endDate;
			flexCodeEdits.userLocalStartTime = this.oneDayOrFlexCodeEdits.startTime;
			flexCodeEdits.userLocalEndTime = this.oneDayOrFlexCodeEdits.endTime;
			// Pass the controlAssignmentId if user has inherited access and selected an assignment
			if (
				this.hasInheritedAccess &&
				this.selectedDeviceAssignments &&
				this.selectedDeviceAssignments.length > 0
			) {
				flexCodeEdits.controlAssignmentId =
					this.selectedDeviceAssignments[0].id;
			}

			let startDateTime = DateTime.fromISO(
				this.datePipe.transform(
					new Date(
						this.oneDayOrFlexCodeEdits.startDate +
							'T' +
							this.oneDayOrFlexCodeEdits.startTime
					),
					'yyyy-MM-ddTHH:mm:ss.SSS'
				) ?? '',
				{ zone: this.locationTimezone }
			);
			flexCodeEdits.utcStartTime = startDateTime.toUTC().toISO()!;

			let endDateTime = DateTime.fromISO(
				this.datePipe.transform(
					new Date(
						this.oneDayOrFlexCodeEdits.endDate +
							'T' +
							this.oneDayOrFlexCodeEdits.endTime
					),
					'yyyy-MM-ddTHH:mm:ss.SSS'
				) ?? '',
				{ zone: this.locationTimezone }
			);
			flexCodeEdits.utcEndTime = endDateTime.toUTC().toISO()!;

			this.apiService
				.requestAndSaveFlexCode(
					flexCodeEdits.deviceId,
					flexCodeEdits,
					this.notifyByEmail,
					this.includeInEmail,
					this.notifyBySms,
					this.includeInSms
				)
				.then((flexCodeGen4) => {
					this.snackbar.open(
						this.isPDQDevice
							? $localize`:@@deviceGenerateOneDayCode-SuccessfullyGeneratedPDQCode:Successfully generated Senturian code.`
							: $localize`:@@deviceGenerateOneDayCode-SuccessfullyGeneratedFlexCode:Successfully generated FlexCode.`,
						undefined,
						{
							duration: 2000,
						}
					);
					this.oneDayOrFlexCodeEdits.oneDayOrFlexCode = flexCodeGen4.flexCode;
					this.oneDayOrFlexCodeEdits.startTime =
						this.oneDayOrFlexCodeEdits.startTime?.replace(/:00$/, '');
					this.oneDayOrFlexCodeEdits.endTime =
						this.oneDayOrFlexCodeEdits.endTime?.replace(/:00$/, '');
					this.codeSuccessfullyGenerated = true;
				})
				.catch((_) => {
					this.snackbar.open(
						this.isPDQDevice
							? $localize`:@@deviceGenerateOneDayCode-NotSuccessfulPDQCodeGen:There was a problem generating your Senturian code. Please try again.`
							: $localize`:@@deviceGenerateOneDayCode-NotSuccessfulFlexCodeGen:There was a problem generating your FlexCode. Please try again.`,
						undefined,
						{
							duration: 2000,
						}
					);
				});
		} else {
			const ondDayCodeEdits = emptyOneDayCodeGen3();
			ondDayCodeEdits.id = this.oneDayOrFlexCodeEdits.id;
			ondDayCodeEdits.deviceId = this.oneDayOrFlexCodeEdits.deviceId;
			ondDayCodeEdits.oneDayCode = this.oneDayOrFlexCodeEdits.oneDayOrFlexCode;
			ondDayCodeEdits.reason = this.oneDayOrFlexCodeEdits.reason;
			ondDayCodeEdits.creatorId = this.oneDayOrFlexCodeEdits.creatorId;
			ondDayCodeEdits.creatorCompanyId =
				this.oneDayOrFlexCodeEdits.creatorCompanyId;
			ondDayCodeEdits.creator = this.oneDayOrFlexCodeEdits.creator;
			ondDayCodeEdits.utcCreateDate = this.oneDayOrFlexCodeEdits.utcCreateDate;
			ondDayCodeEdits.utcValidDate = this.oneDayOrFlexCodeEdits.utcValidDate;
			ondDayCodeEdits.firstName = this.oneDayOrFlexCodeEdits.firstName;
			ondDayCodeEdits.lastName = this.oneDayOrFlexCodeEdits.lastName;
			ondDayCodeEdits.email = this.oneDayOrFlexCodeEdits.email;
			ondDayCodeEdits.mobilePhoneNumber =
				this.oneDayOrFlexCodeEdits.mobilePhoneNumber;
			ondDayCodeEdits.propertyName = this.oneDayOrFlexCodeEdits.propertyName;
			ondDayCodeEdits.deviceLocation =
				this.oneDayOrFlexCodeEdits.deviceLocation;
			ondDayCodeEdits.address = this.oneDayOrFlexCodeEdits.address;
			// Pass the controlAssignmentId if user has inherited access and selected an assignment
			if (
				this.hasInheritedAccess &&
				this.selectedDeviceAssignments &&
				this.selectedDeviceAssignments.length > 0
			) {
				ondDayCodeEdits.controlAssignmentId =
					this.selectedDeviceAssignments[0].id;
			}
			this.apiService
				.requestAndSaveOneDayCode(
					ondDayCodeEdits.deviceId,
					ondDayCodeEdits,
					this.notifyByEmail,
					this.includeInEmail,
					this.notifyBySms,
					this.includeInSms
				)
				.then((oneDayCodeResponse) => {
					this.snackbar.open(
						$localize`:@@generateCode-codeGenerated:Successfully generated OneDayCode.`,
						undefined,
						{
							duration: 2000,
						}
					);
					this.oneDayOrFlexCodeEdits.oneDayOrFlexCode =
						oneDayCodeResponse.data.oneDayCode;
					this.oneDayOrFlexCodeEdits.utcValidDate =
						oneDayCodeResponse.data.utcValidDate ||
						this.oneDayOrFlexCodeEdits.utcValidDate;
					this.companion = oneDayCodeResponse.companion;
					this.companionWindowStartLabel =
						oneDayCodeResponse.companionWindowStartLabel;
					this.codeSuccessfullyGenerated = true;
				})
				.catch((_) => {
					this.snackbar.open(
						$localize`:@@generateCode-codeGenerationError:There was a problem generating your OneDayCode. Please try again.`,
						undefined,
						{
							duration: 2000,
						}
					);
				});
		}
	}

	generateCodes(): void {
		if (this.isHaspCodeGenFeatureEnabled && this.isHaspCodeGenForm) {
			this.generateHaspCode();
		} else {
			this.generateAccessCode();
		}
	}

	returnToDevicesClicked(): void {
		this.dialogRef.close(this.dialogCloseResult());
	}

	get hasCompanionOneDayCode(): boolean {
		return !!this.companion?.oneDayCode;
	}

	private dialogCloseResult(): {
		deviceInfoChanged: boolean;
		codeGenerated: boolean;
	} {
		const deviceInfoChanged =
			this.codeSuccessfullyGenerated &&
			(this.willRemoveFromProperty ||
				this.oneDayOrFlexCodeEdits.newLocation !== '');
		return {
			deviceInfoChanged,
			codeGenerated: this.codeSuccessfullyGenerated,
		};
	}

	async onDeviceSelectedEvent(device: Device): Promise<void> {
		this.resetTabAutomationIds();
		this.selectedDeviceForSync = device?.id ? device : null;
		let selectedDevice = device;

		if (device?.id && !device.codeGenerationAutofill) {
			try {
				selectedDevice = await this.getDeviceById(device.id);
			} catch {
				selectedDevice = device;
			}
		}

		this.device = selectedDevice;
		this.isSentriVaultDeviceFlag = isSentriVaultDevice(this.device) ?? false;
		this.oneDayOrFlexCodeEdits.deviceId = selectedDevice.id;
		this.oneDayOrFlexCodeEdits.utcCreateDate = new Date(
			Date.now()
		).toISOString();

		// we need to set the country code on PhoneNumber object to be passed into the phone-type-form
		this.mobilePhone = {
			countryCode: this.getCountryCodeDefault(),
		};

		this.setTimeDateInLocationTimezone();
		this.applyDefaultDateValues();

		this.isDeviceOwner =
			this.device.activeDeviceAssignment?.companyId ===
			this.userProfile?.activeRoleDetails?.company.id;
		if (this.device.id && this.canIssueFlexCodes && !this.isDeviceOwner) {
			// check if the device is inherited, whether or not they have access to generate codes
			this.fetchingDeviceControlSummaries = true;
			this.hasInheritedAccess = true;
			this.requestDeviceControlSummaries();
		} else {
			this.hasInheritedAccess = false;
		}
	}

	notifyByEmailChanged(event: MatSlideToggleChange): void {
		if (!event.checked) {
			// if the user doesn't want to send an email uncheck all the email related boxes
			this.includeInEmail = {
				address: false,
				propertyName: false,
				deviceLocation: false,
			};
		}
	}

	notifyBySmsChanged(event: MatSlideToggleChange): void {
		if (!event.checked) {
			// if the user doesn't want to send an email uncheck all the email related boxes
			this.includeInSms = {
				address: false,
				propertyName: false,
				deviceLocation: false,
			};
		}
	}

	async requestDeviceControlSummaries(): Promise<void> {
		// check if the device was given code generation access, if so populate the access given so the inheritor company
		// can generate codes only within that timeframe

		// disable these so if the company doesn't have access to create codes, user cannot select date or time
		this.minStartDate = new Date('12/31/2500');
		this.maxStartDate = new Date('12/31/2500');
		this.minEndDate = new Date('12/31/2500');
		this.maxEndDate = new Date('12/31/2500');
		this.startTimeMustBeAfter = '00:00:00';
		this.startTimeMustBeBefore = '00:00:00';
		this.endTimeMustBeAfter = '00:00:00';
		this.endTimeMustBeBefore = '00:00:00';

		this.resultingDeviceAccess = [];
		let deviceControlSummary: ControlResponse[] | undefined;

		deviceControlSummary =
			await this.apiService.getInheritedDeviceGroupControlByDeviceId(
				this.device.id
			);

		if (deviceControlSummary) {
			const queryParameters: QueryParam[] = [
				{ param: 'rowIndex', value: 0 },
				{ param: 'rowCount', value: 20 },
				{ param: 'isParent', value: false }, // this will return relationshipts where the companyId is child
			];
			// get the relationships where the company is Child to and filter the keep the controls with direct inheritence
			const rn = await this.apiService.getCompanyRelationshipsByCompanyId(
				this.userProfile?.activeRoleDetails?.company.id ?? '',
				queryParameters
			);
			// Create a list of parentEntitId that are direct parents to the company
			const parentEntityIds = rn.map(
				(item) => item.entityRelationship.parentEntityId
			);

			deviceControlSummary.forEach((item) => {
				// filter assignments if the company is not a direct parent to the control
				if (!parentEntityIds.includes(item.control.companyId!)) {
					return;
				}
				item.assignments.forEach((val) => {
					const currentDate = new Date();
					const startDate = new Date(val.utcStartDate);
					const endDate = val.utcEndDate ? new Date(val.utcEndDate) : null;
					let pushItem = false;

					if (!(
						currentDate >= startDate &&
						(!endDate || currentDate <= endDate)
					)) {
						return;
					}

					if (val.allowCodeGeneration) {
						this.allowAccessCodeGen = true;
						pushItem = true;
					}
					if (val.allowHaspCodeGeneration) {
						this.allowHaspCodeGen = true;
						pushItem = true;
					}
					if (pushItem) {
						this.resultingDeviceAccess?.push(item);
					}
				});
			});
		}

		this.fetchingDeviceControlSummaries = false;

		// Select the first inherited control by default if any exist and none are selected
		if (
			this.resultingDeviceAccess.length > 0 &&
			!this.resultingDeviceAccess.some((res) => res.control.name === 'selected')
		) {
			this.onSelectedInheritorChangedEvent(this.resultingDeviceAccess[0]);
		}
	}

	onTabChanged = (tabChangeEvent: MatTabChangeEvent): void => {
		// if the user switches to the other tab, reset the isHaspCodeGenForm to true
		if (tabChangeEvent.index === 0) {
			this.isHaspCodeGenForm = false;
			this.willRemoveFromProperty = true;
		} else {
			this.isHaspCodeGenForm = true;
		}
		this.resetDateFields();
		this.applyDefaultDateValues();
	};

	onRemoveToggle(event: boolean): void {
		this.willRemoveFromProperty = event;
	}

	onSelectedInheritorChangedEvent(deviceAccess: {
		control: DeviceControl;
		assignments: ControlAssignment[];
		devices: Device[];
	}): void {
		this.resetTabAutomationIds();
		// Prevent unselecting: if already selected, do nothing (we want to make these like radio buttons)
		if (deviceAccess.control.name === 'selected') {
			return;
		}
		this.selectedDeviceAccess = deviceAccess.control;
		this.selectedDeviceAssignments = deviceAccess.assignments;
		// unselect the other inhertied device controls but setting the control.name to 'notSelected'. This will update the class on html
		this.resultingDeviceAccess?.forEach((res) => {
			if (res.control !== deviceAccess.control) {
				res.control.name = 'notSelected';
			}
		});
		// toggle the selection of mat-grid-tile to fake selection and set background color
		deviceAccess.control.name = 'selected';

		this.oneDayOrFlexCodeEdits.startDate = '';
		this.oneDayOrFlexCodeEdits.endDate = '';
		// if startDate from the control is in the past, set minStartDate and minEndDate to today's Date
		this.minStartDate =
			new Date(deviceAccess.assignments[0].utcStartDate) < new Date()
				? new Date()
				: new Date(deviceAccess.assignments[0].utcStartDate);
		this.maxStartDate = new Date(
			deviceAccess.assignments[0].utcEndDate ?? '12/31/2500'
		);
		this.minEndDate =
			new Date(deviceAccess.assignments[0].utcStartDate) < new Date()
				? new Date()
				: new Date(deviceAccess.assignments[0].utcStartDate);
		this.maxEndDate = new Date(
			deviceAccess.assignments[0].utcEndDate ?? '12/31/2500'
		);

		this.oneDayOrFlexCodeEdits.startTime = '';
		this.oneDayOrFlexCodeEdits.endTime = '';
		this.startTimeMustBeAfter = deviceAccess.control.startTime;
		this.startTimeMustBeBefore = deviceAccess.control.endTime;
		this.endTimeMustBeAfter = this.startTimeMustBeAfter;
		this.endTimeMustBeBefore = deviceAccess.control.endTime;

		// disable the button by default when user selects a different control
		this.disableCodeGenerateButton = true;

		// Check if current selection allows the user to generate certain types of codes
		this.allowAccessCodeGen = false;
		this.allowHaspCodeGen = false;
		deviceAccess.assignments.forEach((val) => {
			const currentDate = new Date();
			const startDate = new Date(val.utcStartDate);
			const endDate = val.utcEndDate ? new Date(val.utcEndDate) : null;

			if (!(currentDate >= startDate && (!endDate || currentDate <= endDate))) {
				return;
			}

			if (val.allowCodeGeneration) {
				this.allowAccessCodeGen = true;
			}
			if (val.allowHaspCodeGeneration) {
				this.allowHaspCodeGen = true;
			}
		});

		if (this.allowAccessCodeGen && this.allowHaspCodeGen) {
			if (this.codeGenTabIndex === 0) {
				this.isHaspCodeGenForm = false;
				this.willRemoveFromProperty = true;
			} else {
				this.isHaspCodeGenForm = true;
			}
		} else if (this.allowHaspCodeGen) {
			this.isHaspCodeGenForm = true;
		} else {
			this.isHaspCodeGenForm = false;
			this.willRemoveFromProperty = true;
		}

		// Clear dates to reset validation when user selects a different control
		this.resetDateFields();
		this.applyDefaultDateValues();
	}

	myFilter = (d: Date | null): boolean => {
		if (d) {
			if (this.hasInheritedAccess) {
				return this.canSelectDay(d, false);
			} else {
				// if the company owns the device, let them select a valid date starting today
				this.minStartDate = new Date();
				this.maxStartDate = new Date('12/31/2050');
			}
		}
		return true;
	};

	resetDateFields() {
		this.oneDayOrFlexCodeEdits.utcValidDate = '';
		this.oneDayOrFlexCodeEdits.startDate = '';
		this.oneDayOrFlexCodeEdits.endDate = '';
		this.oneDayOrFlexCodeEdits.startTime = '';
		this.oneDayOrFlexCodeEdits.endTime = '';
		this.dateTimeControl.setValue('');
		this.dateTimeControl.markAsPristine();
		this.dateTimeControl.markAsUntouched();
		this.resetEndDateControl();
	}

	resetEndDateControl() {
		this.endDateTimeControl.setValue('');
		this.endDateTimeControl.markAsPristine();
		this.endDateTimeControl.markAsUntouched();
	}

	isBefore(time1: string, time2: string): boolean {
		const date1 = new Date(`2000-01-01T${time1}`);
		const date2 = new Date(`2000-01-01T${time2}`);

		return date1 < date2;
	}

	checkIsStartDateToday(): boolean {
		return (
			this.oneDayOrFlexCodeEdits.startDate !== '' &&
			this.oneDayOrFlexCodeEdits.startDate ===
				this.currentDateinLocationTimezone
		);
	}

	showStartAndEndDateTimeframe() {
		return (
			this.device?.deviceGeneration?.toLowerCase()?.includes('gen4') ||
			this.isPDQDevice
		);
	}

	hasValidDeviceSelected(): boolean {
		return !!(
			this.device?.id &&
			this.device?.serialNumber &&
			this.device?.deviceGeneration
		);
	}

	getApiService() {
		return this.apiService;
	}
}

export interface OneDayOrFlexCodeGen {
	id: BigIntId /* ^[\d]*$ */;
	deviceId: BigIntId /* ^[\d]*$ */;
	device?: Device;
	oneDayOrFlexCode?: string;
	creatorId: BigIntId /* ^[\d]*$ */;
	creatorCompanyId: BigIntId /* ^[\d]*$ */;
	creator?: User;
	utcCreateDate: UtcDateTime /* date-time */;
	utcValidDate: UtcDate /* date */;
	startDate: UtcDate /* date */;
	endDate: UtcDate /* date */;
	startTime: string /* date-time */;
	endTime: string /* date-time */;
	reason: string;
	firstName: FirstName;
	lastName: LastName;
	newLocation?: string;
	email?: Email /* email */;
	mobilePhoneNumber?: string;
	locked: boolean;
	propertyName?: string;
	deviceLocation?: string;
	address?: Address;
}

export const emptyCodeGenFields = (): OneDayOrFlexCodeGen => ({
	id: '',
	deviceId: '',
	oneDayOrFlexCode: '',
	reason: '',
	creatorId: '',
	creatorCompanyId: '',
	creator: emptyUser(),
	utcCreateDate: '',
	utcValidDate: '',
	startDate: '',
	endDate: '',
	startTime: '',
	endTime: '',
	firstName: '',
	lastName: '',
	newLocation: '',
	email: '',
	mobilePhoneNumber: '',
	locked: true,
	propertyName: '',
	deviceLocation: '',
	address: emptyAddress(),
});
